import { Assets, ColorMatrixFilter, Container, Sprite } from 'pixi.js';
import type { FighterState, FacingDirection, AttackType } from '@/shared/types';
import { createTRexGraphics, createSpinosaurusGraphics, type DinoParts } from './dino-graphics';

/** Visual scale applied to all dino graphics (root container). */
const DINO_SCALE = 1.5;

export interface FighterDisplayState {
  x: number;
  y: number;
  width: number;
  height: number;
  state: FighterState;
  facing: FacingDirection;
  attackTimer: number;
  currentAttack: AttackType | null;
  color: string;
  isHit: boolean;
}

/**
 * Base positions for head/jaw/eye — must match the positions set in dino-graphics.ts.
 *
 *   T-Rex:  head (14,-88), jaw (6,-88), eye (22,-98)
 *   Spino:  head (14,-98), jaw (8,-98), eye (18,-108)
 */
interface BasePose {
  headX: number;
  headY: number;
  jawX: number;
  eyeX: number;
  eyeY: number;
}

function getBasePose(type: 'trex' | 'spinosaurus'): BasePose {
  return type === 'trex'
    ? { headX: 14, headY: -88, jawX: 6, eyeX: 22, eyeY: -98 }
    : { headX: 14, headY: -98, jawX: 8, eyeX: 18, eyeY: -108 };
}

/**
 * Manages the PixiJS visual representation of a single fighter.
 * Handles animation frames based on fighter state.
 */
/**
 * Body-part approximate world-space offsets (relative to fighter root anchor)
 * used to spawn crumble particles from plausible locations on the dino body.
 */
interface BodyPartOffset {
  /** Name — cosmetic for debugging. */
  name: string;
  /** Horizontal offset from fighter center. */
  dx: number;
  /** Vertical offset from fighter anchor (negative = above). */
  dy: number;
  /** Representative colour of this body region. */
  color: number;
  /** Size of blocks that break off from this region. */
  blockSize: number;
}

/** A single crumble block particle. */
export interface CrumbleParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: number;
  alpha: number;
  life: number;
  maxLife: number;
  rotation: number;
  rotationSpeed: number;
}

/** Result of getKOCrumbleState — tells caller what to spawn & whether effect is active. */
export interface KOCrumbleState {
  /** True when the KO animation has entered the crumble phase. */
  crumbling: boolean;
  /** True during the first frame of crumble (spawn particles now). */
  justStartedCrumble: boolean;
  /** True when the entire KO sequence (fall + crumble) is done. */
  done: boolean;
}

/** Approximate body-part offsets for T-Rex (scaled by DINO_SCALE). */
const TREX_CRUMBLE_PARTS: BodyPartOffset[] = [
  { name: 'head', dx: 21, dy: -135, color: 0x2d8c2d, blockSize: 9 },
  { name: 'jaw', dx: 27, dy: -123, color: 0x2d8c2d, blockSize: 6 },
  { name: 'chest', dx: 0, dy: -105, color: 0x2d8c2d, blockSize: 12 },
  { name: 'belly', dx: -3, dy: -75, color: 0x5cb85c, blockSize: 10.5 },
  { name: 'hips', dx: -6, dy: -52.5, color: 0x2d8c2d, blockSize: 9 },
  { name: 'tail1', dx: -45, dy: -72, color: 0x2d8c2d, blockSize: 7.5 },
  { name: 'tail2', dx: -82.5, dy: -69, color: 0x1a5c1a, blockSize: 6 },
  { name: 'tail3', dx: -105, dy: -66, color: 0x1a5c1a, blockSize: 4.5 },
  { name: 'armL', dx: 27, dy: -97.5, color: 0x2d8c2d, blockSize: 6 },
  { name: 'armR', dx: 33, dy: -94.5, color: 0x2d8c2d, blockSize: 6 },
  { name: 'legL', dx: -15, dy: -18, color: 0x2d8c2d, blockSize: 7.5 },
  { name: 'legR', dx: 12, dy: -18, color: 0x2d8c2d, blockSize: 7.5 },
  { name: 'eye', dx: 33, dy: -147, color: 0xffcc00, blockSize: 4.5 },
];

/** Approximate body-part offsets for Spinosaurus (scaled by DINO_SCALE). */
const SPINO_CRUMBLE_PARTS: BodyPartOffset[] = [
  { name: 'head', dx: 21, dy: -150, color: 0x8c2d2d, blockSize: 9 },
  { name: 'jaw', dx: 30, dy: -138, color: 0x8c2d2d, blockSize: 6 },
  { name: 'sail', dx: 0, dy: -172.5, color: 0xa83020, blockSize: 7.5 },
  { name: 'chest', dx: 0, dy: -117, color: 0x8c2d2d, blockSize: 12 },
  { name: 'belly', dx: -3, dy: -84, color: 0xc75050, blockSize: 10.5 },
  { name: 'hips', dx: -6, dy: -57, color: 0x8c2d2d, blockSize: 9 },
  { name: 'tail1', dx: -45, dy: -78, color: 0x8c2d2d, blockSize: 7.5 },
  { name: 'tail2', dx: -82.5, dy: -75, color: 0x5c1a1a, blockSize: 6 },
  { name: 'tail3', dx: -111, dy: -72, color: 0x5c1a1a, blockSize: 4.5 },
  { name: 'armL', dx: 30, dy: -105, color: 0x8c2d2d, blockSize: 6 },
  { name: 'armR', dx: 36, dy: -102, color: 0x8c2d2d, blockSize: 6 },
  { name: 'legL', dx: -18, dy: -21, color: 0x8c2d2d, blockSize: 7.5 },
  { name: 'legR', dx: 15, dy: -21, color: 0x8c2d2d, blockSize: 7.5 },
  { name: 'eye', dx: 27, dy: -162, color: 0xff6600, blockSize: 4.5 },
];

/**
 * Slight random colour variation — darken or lighten channels by up to `range`.
 * Returns a greyed-out variant (the crumble blocks look like stone/dust).
 */
function variantColor(base: number, range: number): number {
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;

  // Desaturate first (crumble pieces are grayed)
  const avg = Math.round(r * 0.3 + g * 0.59 + b * 0.11);
  const mix = 0.6; // 60% gray, 40% original
  const mr = Math.round(r * (1 - mix) + avg * mix);
  const mg = Math.round(g * (1 - mix) + avg * mix);
  const mb = Math.round(b * (1 - mix) + avg * mix);

  // Random brightness variation
  const variation = 1 + (Math.random() - 0.5) * range * 2;
  const fr = Math.max(0, Math.min(255, Math.round(mr * variation)));
  const fg = Math.max(0, Math.min(255, Math.round(mg * variation)));
  const fb = Math.max(0, Math.min(255, Math.round(mb * variation)));

  return (fr << 16) | (fg << 8) | fb;
}

export class FighterDisplay {
  readonly container: Container;
  private parts: DinoParts;
  private animTimer = 0;
  private hitFlashTimer = 0;
  private base: BasePose;
  private readonly dinoType: 'trex' | 'spinosaurus';

  // Track previous state for transitions
  private prevState: FighterState = 'idle';

  // KO grayscale + crumble state
  private koFilter: ColorMatrixFilter | null = null;
  private koDesaturation = 0; // 0 = full color, 1 = fully gray
  /** Frame count *within* KO state (not reset by animTimer). */
  private koFrameCount = 0;
  /** Whether crumble phase has started (after fall completes). */
  private koCrumbleStarted = false;
  /** Whether the first crumble frame has been consumed by caller. */
  private koCrumbleFirstFrame = false;
  /** Current facing when KO started — needed to compute world positions for particles. */
  private koFacing: FacingDirection = 'right';

  /** Optional head image sprite overlaid on the dino head. */
  private headSprite: Sprite | null = null;

  constructor(type: 'trex' | 'spinosaurus', headImagePath?: string | null) {
    this.container = new Container();
    this.dinoType = type;
    this.base = getBasePose(type);

    if (type === 'trex') {
      this.parts = createTRexGraphics();
    } else {
      this.parts = createSpinosaurusGraphics();
    }

    // Scale the root container so all dino graphics render 1.5x larger.
    // Facing direction in update() will set scale.x to ±DINO_SCALE.
    this.parts.root.scale.set(DINO_SCALE, DINO_SCALE);

    this.container.addChild(this.parts.root);

    // Load head image overlay if provided
    if (headImagePath) {
      this.loadHeadImage(headImagePath);
    }
  }

  /**
   * Load a head image and overlay it on the dino's head.
   * The sprite is added as a child of the head Graphics so it follows all animations.
   */
  private async loadHeadImage(path: string): Promise<void> {
    try {
      const texture = await Assets.load(path);
      this.headSprite = new Sprite(texture);

      // Size the head image to cover the dino head area
      // T-Rex head: roughly 36x24 pixels, Spino head: roughly 52x20 pixels
      const headSize = this.dinoType === 'trex' ? 36 : 44;
      this.headSprite.width = headSize;
      this.headSprite.height = headSize;

      // Anchor at center so it aligns with the head pivot
      this.headSprite.anchor.set(0.5, 0.5);

      // Position relative to the head Graphics' pivot
      // T-Rex head pivot is (8, -12), graphics span roughly from (-10,-24) to (36,-8)
      //   => center of head is roughly (13, -16) relative to origin, shift from pivot: (5, -4)
      // Spino head pivot is (8, -10), graphics span roughly from (-8,-22) to (44,-6)
      //   => center of head is roughly (18, -14) relative to origin, shift from pivot: (10, -4)
      if (this.dinoType === 'trex') {
        this.headSprite.x = 10;
        this.headSprite.y = -14;
      } else {
        this.headSprite.x = 16;
        this.headSprite.y = -12;
      }

      // Add to head so it follows head movement, rotation etc.
      this.parts.head.addChild(this.headSprite);

      // Hide the procedural dino head graphics (cranium/snout rectangles) —
      // keep the Graphics as a transform container so the sprite still renders.
      // this.parts.head.renderable = false;
      // Hide jaw and eye entirely — the photo head replaces them.
      this.parts.jaw.visible = false;
      this.parts.eye.visible = false;
    } catch {
      // Head image failed to load — degrade gracefully (keep dino head)
    }
  }

  /**
   * Update the display to match the current fighter state.
   * Called every frame.
   */
  update(state: FighterDisplayState): void {
    // Position — anchor is center-bottom of fighter
    this.container.x = state.x;
    this.container.y = state.y;

    // State transition — reset animation on state change
    if (state.state !== this.prevState) {
      this.resetTransforms();
      this.prevState = state.state;
      // Track facing at KO start
      if (state.state === 'ko') {
        this.koFacing = state.facing;
      }
    }

    // Facing direction — flip the root horizontally (preserving DINO_SCALE).
    // Must be set AFTER resetTransforms() which resets scale to default.
    const scaleX = state.facing === 'right' ? DINO_SCALE : -DINO_SCALE;
    this.parts.root.scale.x = scaleX;

    // Hit flash
    if (state.isHit) {
      this.hitFlashTimer = 6;
    }
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer--;
      // Flash white by adjusting tint
      this.parts.root.alpha = this.hitFlashTimer % 2 === 0 ? 1 : 0.6;
    } else {
      this.parts.root.alpha = 1;
    }

    // Advance animation timer
    this.animTimer++;

    // Apply animation based on state
    switch (state.state) {
      case 'idle':
        this.animateIdle();
        break;
      case 'walk':
        this.animateWalk();
        break;
      case 'jump':
        this.animateJump();
        break;
      case 'attack':
        this.animateAttack(state.currentAttack, state.attackTimer);
        break;
      case 'block':
        this.animateBlock();
        break;
      case 'hit':
        this.animateHit();
        break;
      case 'ko':
        this.animateKO();
        break;
    }
  }

  private animateIdle(): void {
    const { headY, eyeY } = this.base;

    // Gentle breathing bob
    const bob = Math.sin(this.animTimer * 0.08) * 2;
    this.parts.body.y = bob;
    this.parts.head.y = headY + bob * 0.5;
    this.parts.jaw.y = headY + bob * 0.5;
    this.parts.eye.y = eyeY + bob * 0.5;

    // Slight tail wag
    this.parts.tail.rotation = Math.sin(this.animTimer * 0.06) * 0.05;

    // Reset limb positions
    this.parts.legLeft.rotation = 0;
    this.parts.legRight.rotation = 0;
    this.parts.armLeft.rotation = Math.sin(this.animTimer * 0.1) * 0.05;
    this.parts.armRight.rotation = Math.sin(this.animTimer * 0.1 + 0.5) * 0.05;

    // Jaw closed
    this.parts.jaw.rotation = 0;
  }

  private animateWalk(): void {
    const { headY, eyeY } = this.base;

    // Walk cycle — alternate legs
    const cycle = this.animTimer * 0.15;
    this.parts.legLeft.rotation = Math.sin(cycle) * 0.4;
    this.parts.legRight.rotation = Math.sin(cycle + Math.PI) * 0.4;

    // Arms swing opposite to legs
    this.parts.armLeft.rotation = Math.sin(cycle + Math.PI) * 0.2;
    this.parts.armRight.rotation = Math.sin(cycle) * 0.2;

    // Body slight forward lean and bob
    const walkBob = Math.abs(Math.sin(cycle * 2)) * 2;
    this.parts.body.y = -walkBob;
    this.parts.head.y = headY - walkBob * 0.5;
    this.parts.jaw.y = headY - walkBob * 0.5;
    this.parts.eye.y = eyeY - walkBob * 0.5;

    // Tail counterbalance
    this.parts.tail.rotation = Math.sin(cycle) * 0.1;

    this.parts.jaw.rotation = 0;
  }

  private animateJump(): void {
    const { headY, eyeY } = this.base;

    // Legs tucked
    this.parts.legLeft.rotation = -0.3;
    this.parts.legRight.rotation = -0.2;

    // Arms up
    this.parts.armLeft.rotation = -0.4;
    this.parts.armRight.rotation = -0.3;

    // Tail streams behind
    this.parts.tail.rotation = 0.2;

    // Head tilted up slightly (offset -2 from base)
    this.parts.body.y = 0;
    this.parts.head.y = headY - 2;
    this.parts.jaw.y = headY - 2;
    this.parts.eye.y = eyeY - 2;

    this.parts.jaw.rotation = 0;
  }

  private animateAttack(attackType: AttackType | null, _timer: number): void {
    const progress = Math.min(this.animTimer / 12, 1);

    switch (attackType) {
      case 'bite':
        this.animateBite(progress);
        break;
      case 'tail-swipe':
        this.animateTailSwipe(progress);
        break;
      case 'special':
        this.animateSpecial(progress);
        break;
      default:
        this.animateBite(progress);
    }
  }

  private animateBite(progress: number): void {
    const { headX, headY, jawX, eyeX, eyeY } = this.base;

    // Lunge forward, jaw opens then snaps shut
    const lunge = progress < 0.4 ? progress / 0.4 : 1 - (progress - 0.4) / 0.6;

    // Jaw opens during wind-up, snaps shut at peak
    const jawOpen =
      progress < 0.3 ? progress / 0.3 : progress < 0.5 ? 1 : 1 - (progress - 0.5) / 0.5;
    this.parts.jaw.rotation = jawOpen * 0.35;

    // Head lunges forward
    this.parts.head.x = headX + lunge * 15;
    this.parts.head.y = headY;
    this.parts.jaw.x = jawX + lunge * 15;
    this.parts.jaw.y = headY;
    this.parts.eye.x = eyeX + lunge * 15;
    this.parts.eye.y = eyeY;

    // Body leans forward
    this.parts.body.rotation = lunge * 0.1;
    this.parts.body.y = 0;

    // Tail counterbalance
    this.parts.tail.rotation = -lunge * 0.15;

    // Legs brace
    this.parts.legLeft.rotation = lunge * 0.2;
    this.parts.legRight.rotation = -lunge * 0.1;
  }

  private animateTailSwipe(progress: number): void {
    const { headX, headY, jawX, eyeX, eyeY } = this.base;

    // Turn body, massive tail swing
    const swing = progress < 0.4 ? progress / 0.4 : 1 - (progress - 0.4) / 0.6;

    // Tail whips around
    this.parts.tail.rotation = -swing * 0.8;

    // Body rotates to wind up
    this.parts.body.rotation = -swing * 0.15;
    this.parts.body.y = 0;

    // Head follows body
    this.parts.head.x = headX - swing * 5;
    this.parts.head.y = headY;
    this.parts.jaw.x = jawX - swing * 5;
    this.parts.jaw.y = headY;
    this.parts.eye.x = eyeX - swing * 5;
    this.parts.eye.y = eyeY;

    this.parts.jaw.rotation = 0;

    // Legs brace
    this.parts.legLeft.rotation = -swing * 0.15;
    this.parts.legRight.rotation = swing * 0.2;
  }

  private animateSpecial(progress: number): void {
    const { headX, headY, jawX, eyeX, eyeY } = this.base;

    // Dramatic wind-up then full-body strike
    const phase = progress < 0.35 ? 0 : progress < 0.5 ? 1 : 2;

    if (phase === 0) {
      // Wind up — crouch and rear back
      const t = progress / 0.35;
      this.parts.body.y = t * 8;
      this.parts.body.rotation = -t * 0.15;

      this.parts.head.y = headY + t * 8;
      this.parts.jaw.y = headY + t * 8;
      this.parts.eye.y = eyeY + t * 8;

      this.parts.jaw.rotation = t * 0.4;
      this.parts.tail.rotation = t * 0.2;
      this.parts.legLeft.rotation = t * 0.3;
      this.parts.legRight.rotation = t * 0.3;
    } else if (phase === 1) {
      // Strike — explosive forward
      const t = (progress - 0.35) / 0.15;
      this.parts.body.y = 8 - t * 12;
      this.parts.body.rotation = -0.15 + t * 0.3;

      this.parts.head.x = headX + t * 20;
      this.parts.head.y = headY - t * 5;
      this.parts.jaw.x = jawX + t * 20;
      this.parts.jaw.y = headY - t * 5;
      this.parts.eye.x = eyeX + t * 20;
      this.parts.eye.y = eyeY - t * 5;

      this.parts.jaw.rotation = 0.4 - t * 0.4;
      this.parts.tail.rotation = 0.2 - t * 0.4;
      this.parts.legLeft.rotation = 0.3 - t * 0.5;
      this.parts.legRight.rotation = 0.3 - t * 0.2;
    } else {
      // Recovery
      const t = (progress - 0.5) / 0.5;
      this.parts.body.y = -4 + t * 4;
      this.parts.body.rotation = 0.15 - t * 0.15;

      this.parts.head.x = headX + 20 - t * 20;
      this.parts.head.y = headY - 5 + t * 5;
      this.parts.jaw.x = jawX + 20 - t * 20;
      this.parts.jaw.y = headY - 5 + t * 5;
      this.parts.eye.x = eyeX + 20 - t * 20;
      this.parts.eye.y = eyeY - 5 + t * 5;

      this.parts.jaw.rotation = 0;
      this.parts.tail.rotation = -0.2 + t * 0.2;
      this.parts.legLeft.rotation = -0.2 + t * 0.2;
      this.parts.legRight.rotation = 0.1 - t * 0.1;
    }
  }

  private animateBlock(): void {
    const { headX, headY, jawX, eyeY } = this.base;

    // Hunker down — crouch with arms up
    this.parts.body.y = 5;
    this.parts.body.rotation = -0.05;

    this.parts.head.y = headY + 8;
    this.parts.head.x = headX - 9;
    this.parts.jaw.y = headY + 8;
    this.parts.jaw.x = jawX - 6;
    this.parts.eye.y = eyeY + 8;

    this.parts.jaw.rotation = 0;

    // Arms raised defensively
    this.parts.armLeft.rotation = -0.6;
    this.parts.armRight.rotation = -0.5;

    // Legs braced wide
    this.parts.legLeft.rotation = 0.15;
    this.parts.legRight.rotation = -0.15;

    // Tail low
    this.parts.tail.rotation = 0.1;
  }

  private animateHit(): void {
    const { headX, headY, jawX, eyeX, eyeY } = this.base;

    // Recoil animation
    const recoil = Math.sin(this.animTimer * 0.5) * 0.5;

    this.parts.body.y = 3;
    this.parts.body.rotation = -0.1 + recoil * 0.05;

    this.parts.head.y = headY + 5;
    this.parts.head.x = headX - 5;
    this.parts.jaw.y = headY + 5;
    this.parts.jaw.x = jawX - 5;
    this.parts.eye.y = eyeY + 5;
    this.parts.eye.x = eyeX - 5;

    // Mouth agape
    this.parts.jaw.rotation = 0.2;

    // Arms flung back
    this.parts.armLeft.rotation = 0.4;
    this.parts.armRight.rotation = 0.3;

    this.parts.tail.rotation = 0.15;

    this.parts.legLeft.rotation = -0.1;
    this.parts.legRight.rotation = 0.1;
  }

  private animateKO(): void {
    this.koFrameCount++;

    // Phase 1: Fall over (frames 1-30) — same as before + progressive desaturation
    const fallProgress = Math.min(this.koFrameCount / 30, 1);
    const eased = 1 - Math.pow(1 - fallProgress, 3); // ease-out cubic

    this.parts.root.rotation = eased * 1.2; // Fall forward/backward
    this.parts.root.y = eased * 30; // Drop down (scaled for 1.5x size)

    // Jaw slack open
    this.parts.jaw.rotation = 0.3;

    // Arms limp
    this.parts.armLeft.rotation = 0.6;
    this.parts.armRight.rotation = 0.5;

    // Legs collapse
    this.parts.legLeft.rotation = eased * 0.4;
    this.parts.legRight.rotation = eased * 0.3;

    // Progressive grayscale desaturation during fall
    this.koDesaturation = Math.min(eased * 1.2, 1); // slightly faster than fall
    this.applyDesaturation(this.koDesaturation);

    // Phase 2: Crumble begins when fall is complete + slight delay
    if (this.koFrameCount >= 35 && !this.koCrumbleStarted) {
      this.koCrumbleStarted = true;
      this.koCrumbleFirstFrame = true;
    }

    // Phase 3: Fade out body after crumble starts (frames 35-80)
    if (this.koCrumbleStarted) {
      const fadeProgress = Math.min((this.koFrameCount - 35) / 45, 1);
      // Progressively lower alpha — fighter dissolves
      this.parts.root.alpha = Math.max(0.15, 1 - fadeProgress * 0.85);
      // Also darken via brightness to simulate crumbling stone
      this.applyDarken(fadeProgress * 0.4);
    }
  }

  /**
   * Apply desaturation to the fighter root using ColorMatrixFilter.
   * @param amount 0 = full color, 1 = fully grayscale.
   */
  private applyDesaturation(amount: number): void {
    if (amount <= 0) {
      if (this.koFilter) {
        this.parts.root.filters = [];
        this.koFilter = null;
      }
      return;
    }

    if (!this.koFilter) {
      this.koFilter = new ColorMatrixFilter();
      this.parts.root.filters = [this.koFilter];
    }

    // Reset to identity then apply partial desaturation
    this.koFilter.reset();
    this.koFilter.desaturate();
    // The desaturate() method fully desaturates. We blend by interpolating alpha:
    // Instead of partial desaturation, we use saturate which goes 0-1.
    // Actually let's use the matrix directly for fine control.
    this.koFilter.reset();
    // saturate(0) = full grayscale, saturate(1) = normal.
    // We need to call saturate with (1 - amount) to progressively desaturate.
    this.koFilter.saturate(1 - amount, false);
  }

  /**
   * Apply additional darkening on top of desaturation (for crumble phase).
   */
  private applyDarken(amount: number): void {
    if (!this.koFilter) return;
    // Apply brightness reduction: 1 = normal, lower = darker
    this.koFilter.brightness(1 - amount, true);
  }

  /**
   * Get KO crumble state for this frame.
   * Caller uses this to know when to spawn crumble particles.
   */
  getKOCrumbleState(): KOCrumbleState {
    if (!this.koCrumbleStarted) {
      return { crumbling: false, justStartedCrumble: false, done: false };
    }

    const justStarted = this.koCrumbleFirstFrame;
    if (this.koCrumbleFirstFrame) {
      this.koCrumbleFirstFrame = false; // consume
    }

    const done = this.koFrameCount >= 80;
    return { crumbling: true, justStartedCrumble: justStarted, done };
  }

  /**
   * Generate crumble particles from body parts.
   * Returns particles positioned in world space.
   * @param worldX The fighter's world X position.
   * @param worldY The fighter's world Y position.
   */
  generateCrumbleParticles(worldX: number, worldY: number): CrumbleParticle[] {
    const parts = this.dinoType === 'trex' ? TREX_CRUMBLE_PARTS : SPINO_CRUMBLE_PARTS;
    const facingMul = this.koFacing === 'right' ? 1 : -1;
    const particles: CrumbleParticle[] = [];

    for (const part of parts) {
      // 2-4 blocks per body part
      const count = Math.floor(Math.random() * 3) + 2;
      for (let i = 0; i < count; i++) {
        const px = worldX + part.dx * facingMul + (Math.random() - 0.5) * 10;
        const py = worldY + part.dy + (Math.random() - 0.5) * 8;
        // Blocks mostly fall down and drift outward
        const spreadX = (Math.random() - 0.5) * 3 + part.dx * facingMul * 0.02;
        const spreadY = Math.random() * -3 - 1; // initial upward then gravity
        const life = Math.floor(Math.random() * 30) + 30;
        const size = part.blockSize * (0.5 + Math.random() * 0.8);

        // Slight color variation (darken/lighten)
        const color = variantColor(part.color, 0.15);

        particles.push({
          x: px,
          y: py,
          vx: spreadX,
          vy: spreadY,
          size,
          color,
          alpha: 1,
          life,
          maxLife: life,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
        });
      }
    }

    return particles;
  }

  /**
   * Reset all transforms to default for clean state transitions.
   */
  resetTransforms(): void {
    const { headX, headY, jawX, eyeX, eyeY } = this.base;

    this.parts.root.rotation = 0;
    this.parts.root.y = 0;
    this.parts.root.alpha = 1;
    // Restore scale (facing will set scale.x each frame, but reset to default here)
    this.parts.root.scale.set(DINO_SCALE, DINO_SCALE);
    this.parts.body.y = 0;
    this.parts.body.rotation = 0;

    this.parts.head.x = headX;
    this.parts.head.y = headY;
    this.parts.jaw.x = jawX;
    this.parts.jaw.y = headY;
    this.parts.jaw.rotation = 0;
    this.parts.eye.x = eyeX;
    this.parts.eye.y = eyeY;

    this.parts.tail.rotation = 0;
    this.parts.armLeft.rotation = 0;
    this.parts.armRight.rotation = 0;
    this.parts.legLeft.rotation = 0;
    this.parts.legRight.rotation = 0;

    this.animTimer = 0;
    this.hitFlashTimer = 0;
    this.prevState = 'idle';

    // Clear KO effect state
    this.koFrameCount = 0;
    this.koDesaturation = 0;
    this.koCrumbleStarted = false;
    this.koCrumbleFirstFrame = false;
    if (this.koFilter) {
      this.parts.root.filters = [];
      this.koFilter = null;
    }
  }

  /**
   * Clean up PixiJS objects.
   */
  destroy(): void {
    this.container.destroy({ children: true });
  }
}
